import { useState } from 'react';
import { Modal, Form, Alert } from 'react-bootstrap';
import Button from 'components/base/Button';

interface PutOnHoldModalProps {
  show: boolean;
  handleClose: () => void;
  request: any | null; // Accept any type of request object with asset_name
  onPutOnHold: (reason: string) => Promise<void>;
}

const PutOnHoldModal = ({
  show,
  handleClose,
  request,
  onPutOnHold
}: PutOnHoldModalProps) => {
  const [reason, setReason] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (!reason.trim()) {
      setError('Please provide a reason for putting this request on hold');
      return;
    }

    setLoading(true);
    try {
      await onPutOnHold(reason.trim());
      handleModalClose();
    } catch (err) {
      console.error('Failed to put request on hold:', err);
      setError(
        err instanceof Error ? err.message : 'Failed to put request on hold'
      );
    } finally {
      setLoading(false);
    }
  };

  const handleModalClose = () => {
    setReason('');
    setError(null);
    handleClose();
  };

  return (
    <Modal show={show} onHide={handleModalClose} centered>
      <Modal.Header closeButton className="bg-light">
        <Modal.Title className="fs-6">Put Request On Hold</Modal.Title>
      </Modal.Header>
      <Form onSubmit={handleSubmit}>
        <Modal.Body>
          {error && (
            <Alert variant="danger" className="mb-3">
              <i className="fas fa-exclamation-triangle me-2"></i>
              {error}
            </Alert>
          )}

          <p className="mb-3">
            Are you sure you want to put the request for{' '}
            <strong>"{request?.asset_name || 'this asset'}"</strong> on hold?
          </p>

          <Form.Group className="mb-3">
            <Form.Label>
              Reason for Hold <span className="text-danger">*</span>
            </Form.Label>
            <Form.Control
              as="textarea"
              rows={3}
              value={reason}
              onChange={e => setReason(e.target.value)}
              placeholder="Enter the reason for putting this request on hold..."
              required
              disabled={loading}
            />
            <Form.Text className="text-muted">
              This reason will be visible to the requester
            </Form.Text>
          </Form.Group>

          <Alert variant="warning" className="mb-0">
            <i className="fas fa-info-circle me-2"></i>
            <small>
              The request will be paused and can be resumed later. The requester
              will be notified.
            </small>
          </Alert>
        </Modal.Body>
        <Modal.Footer className="border-top-0">
          <Button
            variant="secondary"
            onClick={handleModalClose}
            disabled={loading}
          >
            Cancel
          </Button>
          <Button variant="warning" type="submit" disabled={loading}>
            {loading ? (
              <>
                <span className="spinner-border spinner-border-sm me-2" />
                Putting On Hold...
              </>
            ) : (
              'Put On Hold'
            )}
          </Button>
        </Modal.Footer>
      </Form>
    </Modal>
  );
};

export default PutOnHoldModal;
